import { Link } from "react-router-dom";
import { Phone, ArrowRight, Check, Bug, Moon, AlertTriangle, CookingPot, ShieldCheck, ClipboardCheck, ClipboardList, SprayCan, Target, CalendarCheck } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Reveal, MaskedLine, SectionTag } from "@/components/Reveal";
import Seo from "@/components/Seo";
import CtaSection from "@/components/CtaSection";
import { CALENDLY_URL } from "@/data/site";

const COCKROACH_IMG = "/assets/cafard.jpg";

const SIGNS = [
    { icon: Moon, title: "Activité nocturne", desc: "Les cafards fuient la lumière. Si vous en voyez en plein jour, l'infestation est déjà avancée." },
    { icon: Bug, title: "Excréments & traces", desc: "Petits points noirs semblables à du poivre, traces grasses le long des plinthes et des plans de travail." },
    { icon: AlertTriangle, title: "Odeur caractéristique", desc: "Une odeur douceâtre et persistante dans la cuisine ou la salle de bain trahit une colonie installée." },
    { icon: CookingPot, title: "Cuisine & humidité", desc: "Chaleur, humidité et restes alimentaires : la blatte germanique adore les cuisines et les arrière-cuisines." },
];

const METHOD = [
    { icon: ClipboardCheck, num: "01", title: "Inspection ciblée", desc: "Localisation des foyers d'infestation : derrière les électroménagers, sous les éviers, gaines et faux plafonds." },
    { icon: Target, num: "02", title: "Gel insecticide pro", desc: "Application de gel appât professionnel dans les zones de passage. Les cafards contaminés éliminent toute la colonie." },
    { icon: SprayCan, num: "03", title: "Pulvérisation rémanente", desc: "Traitement des zones critiques avec des insecticides professionnels à effet longue durée." },
    { icon: ShieldCheck, num: "04", title: "Monitoring & suivi", desc: "Pose de pièges de contrôle, second passage et conseils d'hygiène pour éviter toute réinfestation." },
];

const PRICING = [
    { name: "Gel anti-cafards", price: "59 €", desc: "Particuliers : cuisine, salle d'eau, WC. Gel professionnel à effet direct et durable.", testid: "cafards-pricing-gel" },
    { name: "Pulvérisation chimique", price: "dès 170 €", desc: "Tarif selon superficie (0 à 150 m²). Éradication rapide et efficace des infestations.", highlight: true, testid: "cafards-pricing-chimique" },
    { name: "Pros & associations", price: "sur devis", desc: "Hôtels, restaurants, associations. Contrats annuels de protection disponibles.", testid: "cafards-pricing-pro" },
];

const FAQ = [
    {
        q: "Comment reconnaître une infestation de cafards ?",
        a: "Insectes bruns aperçus la nuit, petits points noirs (excréments), oothèques (capsules d'œufs) brunes, odeur douceâtre persistante et traces grasses le long des murs sont les signes les plus courants.",
    },
    {
        q: "Les cafards sont-ils dangereux pour la santé ?",
        a: "Oui. Ils transportent des bactéries (salmonelles, E. coli), contaminent les aliments et leurs déjections peuvent déclencher allergies et asthme, en particulier chez les enfants.",
    },
    {
        q: "Combien de passages sont nécessaires ?",
        a: "Deux passages espacés d'environ 15 jours sont généralement nécessaires pour éliminer les adultes puis les jeunes issus des œufs. Les pièges de monitoring confirment l'éradication.",
    },
    {
        q: "Puis-je rester chez moi pendant le traitement ?",
        a: "Avec le traitement au gel, oui dans la plupart des cas. Pour la pulvérisation, une évacuation de quelques heures peut être recommandée, notamment pour les enfants, les animaux et les personnes fragiles.",
    },
];

const Cafards = () => {
    return (
        <div data-testid="cafards-page">
            <Seo
                title="Extermination cafards & blattes en Bretagne | Dès 59 € | Eradikbreizh"
                description="Traitement anti-cafards : gel professionnel, pulvérisation rémanente, monitoring. Intervention sous 2h, 7j/7 en Bretagne. Devis gratuit au 02 55 99 22 43."
            />
            {/* HERO */}
            <section className="relative overflow-hidden bg-gradient-to-b from-mist to-white pb-24 pt-40 md:pb-32 md:pt-48">
                <div className="absolute -left-40 top-0 h-[30rem] w-[30rem] rounded-full bg-brand/10 blur-[160px]" aria-hidden="true" />
                <div className="mx-auto grid max-w-7xl items-center gap-16 px-5 sm:px-8 lg:grid-cols-2">
                    <div>
                        <MaskedLine delay={0.1}>
                            <span className="inline-flex items-center gap-3 rounded-full border border-black/10 bg-white px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-slate-600 shadow-sm">
                                <Bug className="h-4 w-4 text-brand" />
                                Spécialiste cafards & blattes
                            </span>
                        </MaskedLine>
                        <h1 className="mt-8 font-display text-5xl font-black uppercase leading-[0.95] tracking-tighter sm:text-7xl">
                            <MaskedLine delay={0.25}>Cafards</MaskedLine>
                            <MaskedLine delay={0.4}>
                                <span className="text-brand">& blattes</span>
                            </MaskedLine>
                        </h1>
                        <MaskedLine delay={0.55}>
                            <p className="mt-8 max-w-lg text-base leading-relaxed text-slate-600 sm:text-lg">
                                Une colonie de cafards double chaque mois. Gel professionnel, pulvérisation
                                rémanente et monitoring : nous éliminons la source, pas seulement les symptômes.
                            </p>
                        </MaskedLine>
                        <MaskedLine delay={0.7}>
                            <div className="mt-10 flex flex-wrap gap-4">
                                <Link
                                    data-testid="cafards-hero-devis-button"
                                    to="/tarifs"
                                    className="inline-flex items-center gap-2 rounded-full bg-brand px-8 py-4 font-bold text-white transition-[background-color,transform] duration-300 hover:bg-brand-hover hover:scale-105 active:scale-95"
                                >
                                    Voir les tarifs
                                    <ArrowRight className="h-5 w-5" />
                                </Link>
                                <a
                                    data-testid="cafards-hero-phone-button"
                                    href="tel:+33255992243"
                                    className="inline-flex items-center gap-2 rounded-full border border-black/15 px-8 py-4 font-bold text-night transition-[border-color,background-color] duration-300 hover:border-brand hover:bg-black/5"
                                >
                                    <Phone className="h-5 w-5 text-brand" />
                                    02 55 99 22 43
                                </a>
                            </div>
                        </MaskedLine>
                    </div>
                    <Reveal delay={0.3} className="relative">
                        <div className="float-slow relative overflow-hidden rounded-3xl border border-black/10 shadow-[0_30px_80px_rgba(10,15,36,0.15)]">
                            <img src={COCKROACH_IMG} alt="Cafard en gros plan" className="h-[26rem] w-full object-cover sm:h-[30rem]" />
                            <div className="absolute inset-0 bg-gradient-to-t from-night/80 via-transparent to-transparent" />
                            <div className="absolute bottom-6 left-6 rounded-2xl border border-black/10 bg-white/85 px-5 py-4 backdrop-blur-xl">
                                <p className="font-display text-2xl font-black text-brand">2 passages</p>
                                <p className="text-xs uppercase tracking-widest text-slate-600">Éradication complète</p>
                            </div>
                        </div>
                    </Reveal>
                </div>
            </section>

            {/* SIGNS */}
            <section className="bg-mist py-24 md:py-32">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal><SectionTag>Reconnaître l'ennemi</SectionTag></Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-6 max-w-2xl font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                            Les signes d'une <span className="text-brand">infestation</span>
                        </h2>
                    </Reveal>
                    <div className="mt-16 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
                        {SIGNS.map((s, i) => (
                            <Reveal key={s.title} delay={0.1 * i} className="h-full">
                                <div className="h-full rounded-3xl border border-black/10 bg-white p-8 shadow-sm transition-colors duration-500 hover:border-brand/40">
                                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand/10 text-brand">
                                        <s.icon className="h-6 w-6" />
                                    </div>
                                    <h3 className="mt-6 font-display text-xl font-bold">{s.title}</h3>
                                    <p className="mt-3 text-sm leading-relaxed text-slate-600">{s.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* METHOD */}
            <section className="py-24 md:py-32">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal><SectionTag>Notre méthode</SectionTag></Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-6 max-w-2xl font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                            Éliminer la colonie, <span className="text-brand">pas seulement les cafards visibles</span>
                        </h2>
                    </Reveal>
                    <div className="mt-16 grid gap-8 md:grid-cols-2">
                        {METHOD.map((m, i) => (
                            <Reveal key={m.num} delay={0.1 * i}>
                                <div
                                    data-testid={`cafards-method-${m.num}`}
                                    className="group relative overflow-hidden rounded-3xl border border-black/10 bg-white p-8 shadow-sm transition-colors duration-500 hover:border-brand/50 sm:p-10"
                                >
                                    <span className="absolute -right-4 -top-8 font-display text-[8rem] font-black leading-none text-night/5 transition-colors duration-500 group-hover:text-brand/15">
                                        {m.num}
                                    </span>
                                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-brand/10 text-brand">
                                        <m.icon className="h-6 w-6" />
                                    </div>
                                    <h3 className="mt-6 font-display text-2xl font-black uppercase tracking-tight">{m.title}</h3>
                                    <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-600 sm:text-base">{m.desc}</p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* PROTOCOLE */}
            <section className="bg-white pb-20">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal>
                        <Link
                            to="/protocole-cafards"
                            data-testid="cafards-protocole-banner"
                            className="flex items-center justify-between gap-6 rounded-3xl border border-black/10 bg-white p-8 shadow-sm transition-[border-color,transform] duration-300 hover:border-brand/50 hover:scale-[1.01]"
                        >
                            <div className="flex items-center gap-4">
                                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand/10 text-brand">
                                    <ClipboardList className="h-6 w-6" />
                                </div>
                                <div>
                                    <p className="font-display text-xl font-black uppercase tracking-tight">Protocole avant & après traitement</p>
                                    <p className="mt-1 text-sm text-slate-600">Préparation obligatoire du logement, à lire avant notre intervention.</p>
                                </div>
                            </div>
                            <ArrowRight className="h-6 w-6 shrink-0 text-brand" />
                        </Link>
                    </Reveal>
                </div>
            </section>

            {/* PRICING */}
            <section className="bg-mist py-24 md:py-32">
                <div className="mx-auto max-w-7xl px-5 sm:px-8">
                    <Reveal><SectionTag>Tarifs indicatifs</SectionTag></Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-6 font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                            Des prix clairs, <span className="text-brand">sans surprise</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.15}>
                        <p className="mt-6 max-w-xl text-sm text-slate-500 sm:text-base">
                            Tarifs indicatifs HT (TVA non applicable, art. 293 B du CGI). Votre devis est gratuit et personnalisé après diagnostic.
                        </p>
                    </Reveal>
                    <div className="mt-16 grid gap-8 lg:grid-cols-3">
                        {PRICING.map((p, i) => (
                            <Reveal key={p.name} delay={0.12 * i} className="h-full">
                                <div
                                    data-testid={p.testid}
                                    className={`flex h-full flex-col rounded-3xl border p-8 transition-[border-color,transform] duration-500 hover:-translate-y-2 ${
                                        p.highlight
                                            ? "border-brand bg-brand text-white shadow-[0_20px_60px_rgba(27,67,189,0.35)]"
                                            : "border-black/10 bg-white shadow-sm hover:border-brand/40"
                                    }`}
                                >
                                    <h3 className={`font-display text-2xl font-black uppercase tracking-tight ${p.highlight ? "text-white" : "text-night"}`}>{p.name}</h3>
                                    <p className={`mt-4 font-display text-4xl font-black ${p.highlight ? "text-white" : "text-brand"}`}>{p.price}</p>
                                    <p className={`mt-3 flex-1 text-sm ${p.highlight ? "text-white/85" : "text-slate-600"}`}>{p.desc}</p>
                                    <a
                                        data-testid={`${p.testid}-booking-button`}
                                        href={CALENDLY_URL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`mt-8 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-bold transition-[background-color,transform] duration-300 hover:scale-[1.03] active:scale-95 ${
                                            p.highlight
                                                ? "bg-white text-brand hover:bg-slate-100"
                                                : "bg-brand text-white hover:bg-brand-hover"
                                        }`}
                                    >
                                        <CalendarCheck className="h-4 w-4" />
                                        Réserver en ligne en 2 min
                                    </a>
                                    <Link
                                        data-testid={`${p.testid}-button`}
                                        to="/tarifs"
                                        className={`mt-3 inline-flex items-center justify-center gap-2 text-xs font-semibold underline-offset-4 transition-colors hover:underline ${
                                            p.highlight ? "text-white/85" : "text-slate-500 hover:text-brand"
                                        }`}
                                    >
                                        Voir la grille
                                        <ArrowRight className="h-3.5 w-3.5" />
                                    </Link>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-24 md:py-32">
                <div className="mx-auto max-w-4xl px-5 sm:px-8">
                    <Reveal><SectionTag>Questions fréquentes</SectionTag></Reveal>
                    <Reveal delay={0.1}>
                        <h2 className="mt-6 font-display text-4xl font-black uppercase leading-[1.02] tracking-tight sm:text-5xl">
                            Tout savoir sur les <span className="text-brand">cafards</span>
                        </h2>
                    </Reveal>
                    <Reveal delay={0.2} className="mt-12">
                        <Accordion type="single" collapsible className="space-y-4" data-testid="cafards-faq">
                            {FAQ.map((item, i) => (
                                <AccordionItem
                                    key={i}
                                    value={`item-${i}`}
                                    data-testid={`cafards-faq-item-${i + 1}`}
                                    className="rounded-2xl border border-black/10 bg-white px-6 shadow-sm transition-colors data-[state=open]:border-brand/40"
                                >
                                    <AccordionTrigger className="py-6 text-left font-display text-lg font-bold hover:no-underline">
                                        {item.q}
                                    </AccordionTrigger>
                                    <AccordionContent className="pb-6 text-sm leading-relaxed text-slate-600 sm:text-base">
                                        {item.a}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </Reveal>
                </div>
            </section>

            <CtaSection
                title="Des cafards dans votre cuisine ?"
                subtitle="Plus vous attendez, plus la colonie grandit. Diagnostic gratuit et intervention sous 2h, 7j/7 partout en Bretagne."
            />
        </div>
    );
};

export default Cafards;
